import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { setAuthToken, refreshAuthToken, AUTH_UPDATED_EVENT, AUTH_EXPIRED_EVENT } from "../services/api.service";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useProjectStore } from "../stores/project.store";

interface AuthContextType {
  auth: { accessToken: string; refreshToken?: string; user: string } | null;
  setAuth: (auth: { accessToken: string; refreshToken?: string; user: string } | null) => void;
  login: (auth: { accessToken: string; refreshToken?: string; user: string }) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const AUTH_KEY = "auth";

  const [auth, setAuth] = useState<{
    accessToken: string;
    refreshToken?: string;
    user: string;
  } | null>(() => {
    const stored = localStorage.getItem(AUTH_KEY);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      return typeof parsed?.accessToken === "string" && parsed.accessToken
        ? parsed
        : null;
    } catch {
      localStorage.removeItem(AUTH_KEY);
      return null;
    }
  });

  const isAuthenticated = !!auth?.accessToken;

  const login = useCallback(
    (authData: { accessToken: string; refreshToken?: string; user: string }) => {
      localStorage.removeItem("project-storage");
      useProjectStore.getState().resetForUser();
      setAuth(authData);
      localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
      setAuthToken(authData.accessToken);
    },
    [],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("project-storage");
    useProjectStore.getState().resetForUser();
    setAuth(null);
    localStorage.removeItem(AUTH_KEY);
    setAuthToken(null);
    if (window.location.pathname !== "/auth") {
      navigate("/auth", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const update = (event: Event) => setAuth((event as CustomEvent).detail);
    window.addEventListener(AUTH_UPDATED_EVENT, update);
    window.addEventListener(AUTH_EXPIRED_EVENT, logout);
    return () => {
      window.removeEventListener(AUTH_UPDATED_EVENT, update);
      window.removeEventListener(AUTH_EXPIRED_EVENT, logout);
    };
  }, [logout]);

  // Persist auth to localStorage
  useEffect(() => {
    if (auth) {
      localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
      setAuthToken(auth.accessToken);
    } else {
      localStorage.removeItem(AUTH_KEY);
      setAuthToken(null);
    }
  }, [auth]);

  // Auto logout on token expiration
  useEffect(() => {
    if (!auth?.accessToken) return;

    let intervalId: NodeJS.Timeout;

    try {
      const payload = jwtDecode<{ exp: number }>(auth.accessToken);
      const expiry = payload.exp * 1000;
      if (!Number.isFinite(expiry)) throw new Error("Missing token expiry");

      const checkExpiry = () => {
        if (Date.now() >= expiry) {
          refreshAuthToken().catch(() => { /* Invalid refresh credentials trigger logout via the auth event. */ });
        }
      };

      checkExpiry();
      intervalId = setInterval(checkExpiry, 60000);
    } catch (e) {
      console.error("Invalid token", e);
      logout();
    }

    return () => clearInterval(intervalId);
  }, [auth, logout]);

  const value = {
    auth,
    setAuth,
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
